import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Slider } from "./ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { AlertTriangle, CheckCircle, Shield, Calculator, Wallet, Clock, CalendarDays, Train, MapPin, Lightbulb, Search } from "lucide-react";

// Minimum wage data for all 47 prefectures (2025 estimate)
const prefectureWages = {
  "hokkaido": { name: "北海道", wage: 1010 },
  "aomori": { name: "青森県", wage: 953 },
  "iwate": { name: "岩手県", wage: 952 },
  "miyagi": { name: "宮城県", wage: 973 },
  "akita": { name: "秋田県", wage: 951 },
  "yamagata": { name: "山形県", wage: 955 },
  "fukushima": { name: "福島県", wage: 955 },
  "ibaraki": { name: "茨城県", wage: 1005 },
  "tochigi": { name: "栃木県", wage: 1004 },
  "gunma": { name: "群馬県", wage: 985 },
  "saitama": { name: "埼玉県", wage: 1078 },
  "chiba": { name: "千葉県", wage: 1076 },
  "tokyo": { name: "東京都", wage: 1163 },
  "kanagawa": { name: "神奈川県", wage: 1162 },
  "niigata": { name: "新潟県", wage: 985 },
  "toyama": { name: "富山県", wage: 998 },
  "ishikawa": { name: "石川県", wage: 984 },
  "fukui": { name: "福井県", wage: 984 },
  "yamanashi": { name: "山梨県", wage: 988 },
  "nagano": { name: "長野県", wage: 998 },
  "gifu": { name: "岐阜県", wage: 1001 },
  "shizuoka": { name: "静岡県", wage: 1034 },
  "aichi": { name: "愛知県", wage: 1077 },
  "mie": { name: "三重県", wage: 1023 },
  "shiga": { name: "滋賀県", wage: 1017 },
  "kyoto": { name: "京都府", wage: 1058 },
  "osaka": { name: "大阪府", wage: 1114 },
  "hyogo": { name: "兵庫県", wage: 1052 },
  "nara": { name: "奈良県", wage: 986 },
  "wakayama": { name: "和歌山県", wage: 980 },
  "tottori": { name: "鳥取県", wage: 957 },
  "shimane": { name: "島根県", wage: 962 },
  "okayama": { name: "岡山県", wage: 982 },
  "hiroshima": { name: "広島県", wage: 1020 },
  "yamaguchi": { name: "山口県", wage: 979 },
  "tokushima": { name: "徳島県", wage: 955 },
  "kagawa": { name: "香川県", wage: 970 },
  "ehime": { name: "愛媛県", wage: 956 },
  "kochi": { name: "高知県", wage: 952 },
  "fukuoka": { name: "福岡県", wage: 992 },
  "saga": { name: "佐賀県", wage: 956 },
  "nagasaki": { name: "長崎県", wage: 953 },
  "kumamoto": { name: "熊本県", wage: 952 },
  "oita": { name: "大分県", wage: 954 },
  "miyazaki": { name: "宮崎県", wage: 952 },
  "kagoshima": { name: "鹿児島県", wage: 953 },
  "okinawa": { name: "沖縄県", wage: 952 },
};

const TaxSimulator = () => {
  const [hourlyWage, setHourlyWage] = useState(1150);
  const [transportFee, setTransportFee] = useState(0);
  const [daysPerWeek, setDaysPerWeek] = useState(3);
  const [hoursPerDay, setHoursPerDay] = useState(5);
  const [selectedPrefecture, setSelectedPrefecture] = useState("");

  const calculations = useMemo(() => {
    const annualIncome = hourlyWage * hoursPerDay * daysPerWeek * 52;
    const annualTransport = transportFee * daysPerWeek * 52;
    const totalTakeHome = annualIncome + annualTransport;

    let status = "safe";
    let statusMessage = "";
    let statusColor = "";
    let statusBg = "";
    let statusBorder = "";

    if (annualIncome > 1300000) {
      status = "danger";
      statusMessage = "危険！社会保険の加入義務が発生します。";
      statusColor = "text-purple-700";
      statusBg = "bg-purple-50";
      statusBorder = "border-purple-300";
    } else if (annualIncome > 1030000) {
      status = "warning";
      statusMessage = "注意！103万円の壁を超えています。";
      statusColor = "text-red-700";
      statusBg = "bg-red-50";
      statusBorder = "border-red-300";
    } else {
      status = "safe";
      statusMessage = "安全！扶養控除の範囲内です。";
      statusColor = "text-green-700";
      statusBg = "bg-green-50";
      statusBorder = "border-green-300";
    }

    return {
      annualIncome,
      annualTransport,
      totalTakeHome,
      status,
      statusMessage,
      statusColor,
      statusBg,
      statusBorder,
    };
  }, [hourlyWage, transportFee, daysPerWeek, hoursPerDay]);

  // Wage comparison logic
  const wageComparison = useMemo(() => {
    if (!selectedPrefecture || !prefectureWages[selectedPrefecture]) {
      return null;
    }

    const prefData = prefectureWages[selectedPrefecture];
    const minWage = prefData.wage;
    const diff = hourlyWage - minWage;

    let status = "good";
    let message = "";
    let bgColor = "";
    let borderColor = "";
    let textColor = "";
    let icon = "check";

    if (hourlyWage < minWage) {
      status = "warning";
      message = `⚠️ 警告: ${prefData.name}の最低賃金を下回っています！`;
      bgColor = "bg-red-50";
      borderColor = "border-red-300";
      textColor = "text-red-700";
      icon = "alert";
    } else if (hourlyWage < minWage + 200) {
      status = "info";
      message = `💡 情報: 最低賃金に近い水準です。より高時給の仕事も探しやすいかもしれません。`;
      bgColor = "bg-yellow-50";
      borderColor = "border-yellow-300";
      textColor = "text-yellow-700";
      icon = "info";
    } else {
      status = "good";
      message = `✅ 良好: 最低賃金を大きく上回っています。`;
      bgColor = "bg-green-50";
      borderColor = "border-green-300";
      textColor = "text-green-700";
      icon = "check";
    }

    return {
      prefName: prefData.name,
      minWage,
      diff,
      status,
      message,
      bgColor,
      borderColor,
      textColor,
      icon,
    };
  }, [selectedPrefecture, hourlyWage]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("ja-JP").format(amount);
  };

  const getStatusIcon = () => {
    switch (calculations.status) {
      case "danger":
        return <Shield className="w-6 h-6" />;
      case "warning":
        return <AlertTriangle className="w-6 h-6" />;
      default:
        return <CheckCircle className="w-6 h-6" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50/50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-blue-100 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-600 rounded-lg">
              <Calculator className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">103万円の壁</h1>
              <p className="text-sm text-gray-500">アルバイト税金シミュレーター</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Results Card - At the very top */}
        <Card className="border-2 border-blue-200 shadow-lg overflow-hidden">
          <CardHeader className="bg-blue-600 text-white pb-4">
            <CardTitle className="text-center text-lg">年間収入（税計算用）</CardTitle>
          </CardHeader>
          <CardContent className="pt-6 pb-4">
            <div className="text-center mb-4">
              <span className="text-4xl md:text-5xl font-bold text-gray-900">
                ¥{formatCurrency(calculations.annualIncome)}
              </span>
            </div>

            {/* Status Alert */}
            <div
              className={`p-4 rounded-xl border-2 ${calculations.statusBg} ${calculations.statusBorder} transition-all duration-300`}
            >
              <div className={`flex items-center justify-center gap-3 ${calculations.statusColor}`}>
                {getStatusIcon()}
                <span className="font-bold text-lg">{calculations.statusMessage}</span>
              </div>

              {/* CTA Button - Only show when status is safe (green) */}
              {calculations.status === "safe" && (
                <div className="mt-4 text-center">
                  <a
                    href="https://www.google.com/search?q=バイト+高時給"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                  >
                    <Search className="w-5 h-5" />
                    稼げるバイトを探す
                  </a>
                </div>
              )}
            </div>

            {/* Additional Info */}
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500 mb-1">交通費（年間）</p>
                <p className="font-semibold text-gray-700">¥{formatCurrency(calculations.annualTransport)}</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <p className="text-xs text-blue-600 mb-1">手取り合計（年間）</p>
                <p className="font-semibold text-blue-700">¥{formatCurrency(calculations.totalTakeHome)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Input Card */}
        <Card className="shadow-md border border-gray-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-gray-800 flex items-center gap-2">
              <Wallet className="w-5 h-5 text-blue-600" />
              勤務条件を入力
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Hourly Wage */}
            <div className="space-y-2">
              <Label htmlFor="hourlyWage" className="flex items-center gap-2 text-gray-700">
                <span className="text-lg">💰</span>
                時給（円）
              </Label>
              <Input
                id="hourlyWage"
                type="number"
                value={hourlyWage}
                onChange={(e) => setHourlyWage(Number(e.target.value) || 0)}
                className="text-lg font-medium h-12 border-2 border-gray-200 focus:border-blue-500 transition-colors"
                min={0}
              />
            </div>

            {/* Transport Fee */}
            <div className="space-y-2">
              <Label htmlFor="transportFee" className="flex items-center gap-2 text-gray-700">
                <Train className="w-5 h-5 text-blue-600" />
                交通費（1日あたり・円）
              </Label>
              <Input
                id="transportFee"
                type="number"
                value={transportFee}
                onChange={(e) => setTransportFee(Number(e.target.value) || 0)}
                className="text-lg font-medium h-12 border-2 border-gray-200 focus:border-blue-500 transition-colors"
                min={0}
              />
              <p className="text-xs text-gray-500">※ 交通費は税計算に含まれません</p>
            </div>

            {/* Days per Week Slider */}
            <div className="space-y-4">
              <Label className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-gray-700">
                  <CalendarDays className="w-5 h-5 text-blue-600" />
                  週の勤務日数
                </span>
                <span className="text-xl font-bold text-blue-600 bg-blue-50 px-3 py-1 rounded-lg">
                  {daysPerWeek}日
                </span>
              </Label>
              <Slider
                value={[daysPerWeek]}
                onValueChange={(value) => setDaysPerWeek(value[0])}
                min={1}
                max={7}
                step={1}
                className="py-2"
              />
              <div className="flex justify-between text-xs text-gray-400">
                <span>1日</span>
                <span>7日</span>
              </div>
            </div>

            {/* Hours per Day Slider */}
            <div className="space-y-4">
              <Label className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-gray-700">
                  <Clock className="w-5 h-5 text-blue-600" />
                  1日の勤務時間
                </span>
                <span className="text-xl font-bold text-blue-600 bg-blue-50 px-3 py-1 rounded-lg">
                  {hoursPerDay}時間
                </span>
              </Label>
              <Slider
                value={[hoursPerDay]}
                onValueChange={(value) => setHoursPerDay(value[0])}
                min={1}
                max={12}
                step={1}
                className="py-2"
              />
              <div className="flex justify-between text-xs text-gray-400">
                <span>1時間</span>
                <span>12時間</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Threshold Reference Card */}
        <Card className="shadow-md border border-gray-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-gray-800">収入の壁について</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                <div>
                  <p className="font-medium text-green-700">103万円以下</p>
                  <p className="text-sm text-green-600">所得税がかからず、扶養控除の対象</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg border border-red-200">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
                <div>
                  <p className="font-medium text-red-700">103万円超〜130万円</p>
                  <p className="text-sm text-red-600">所得税が発生、扶養控除対象外に</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                <Shield className="w-5 h-5 text-purple-600 flex-shrink-0" />
                <div>
                  <p className="font-medium text-purple-700">130万円超</p>
                  <p className="text-sm text-purple-600">社会保険の加入義務が発生</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Prefecture Wage Comparison Card */}
        <Card className="shadow-md border border-gray-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-gray-800 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-600" />
              最低賃金との比較
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-700">お住まいの地域を選択</Label>
              <Select value={selectedPrefecture} onValueChange={setSelectedPrefecture}>
                <SelectTrigger className="w-full h-12 border-2 border-gray-200 focus:border-blue-500">
                  <SelectValue placeholder="都道府県を選択してください" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {Object.entries(prefectureWages).map(([key, data]) => (
                    <SelectItem key={key} value={key}>
                      {data.name}（¥{data.wage}）
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Comparison Result Card with Animation */}
            {wageComparison && (
              <div
                className="animate-in fade-in slide-in-from-bottom-2 duration-300"
                key={selectedPrefecture}
              >
                <div className={`p-4 rounded-xl border-2 ${wageComparison.bgColor} ${wageComparison.borderColor}`}>
                  {/* Wage Comparison */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="text-center p-3 bg-white/60 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">{wageComparison.prefName}の最低賃金</p>
                      <p className="text-2xl font-bold text-gray-800">¥{formatCurrency(wageComparison.minWage)}</p>
                    </div>
                    <div className="text-center p-3 bg-white/60 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">あなたの時給</p>
                      <p className="text-2xl font-bold text-blue-600">¥{formatCurrency(hourlyWage)}</p>
                    </div>
                  </div>

                  {/* Difference */}
                  <div className="text-center mb-3">
                    <span className="text-sm text-gray-600">差額: </span>
                    <span className={`font-bold ${wageComparison.diff >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {wageComparison.diff >= 0 ? '+' : ''}{formatCurrency(wageComparison.diff)}円
                    </span>
                  </div>

                  {/* Status Message */}
                  <div className={`p-3 rounded-lg bg-white/80 ${wageComparison.textColor}`}>
                    <p className="font-medium text-center">{wageComparison.message}</p>
                  </div>
                </div>
              </div>
            )}

            {!selectedPrefecture && (
              <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 text-center">
                <Lightbulb className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">
                  都道府県を選択すると、最低賃金との比較結果が表示されます
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Detailed SEO Section - 交通費の扱い */}
        <div className="max-w-3xl mx-auto px-4 py-8 text-slate-700 leading-relaxed">
          <h2 className="text-2xl font-bold mb-4 text-slate-800">交通費は含まれる？よくある勘違い</h2>
          <p className="mb-4">
            多くの人が迷うのが「交通費」の扱いです。実は、壁の種類によってルールが異なります。
          </p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li><strong>103万円の壁（税金）：</strong> 原則として交通費は<strong>「含まれません」</strong>（非課税限度額内の場合）。つまり、時給だけで103万円を超えなければセーフです。</li>
            <li><strong>130万円の壁（社会保険）：</strong> こちらは交通費を<strong>「含んだ総支給額」</strong>で判断されます。ここが最大の落とし穴です。</li>
          </ul>
          <p>
            当ツールでは、この複雑な計算を自動判定しています。「ギリギリ超えてしまった！」という失敗を防ぐために、こまめにチェックすることをお勧めします。
          </p>
        </div>

        {/* SEO Placeholder */}
        <div id="seo-content" className="py-8">
          <div className="prose prose-gray max-w-none">
            <h2 className="text-xl font-bold text-gray-800 mb-4">103万円の壁とは？</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              103万円の壁とは、年間の給与収入が103万円を超えると所得税が発生し、親や配偶者の扶養控除の対象から外れてしまう収入のボーダーラインのことです。
            </p>
            <p className="text-gray-600 leading-relaxed mb-4">
              アルバイトやパートで働く学生や主婦の方にとって、この103万円という金額は非常に重要です。この金額を超えると、自分自身に所得税がかかるだけでなく、扶養している家族の税金も増えてしまう可能性があります。
            </p>
            <h3 className="text-lg font-bold text-gray-800 mb-3 mt-6">130万円の壁について</h3>
            <p className="text-gray-600 leading-relaxed">
              年収が130万円を超えると、社会保険（健康保険・厚生年金）への加入義務が発生します。これにより、手取り収入が大幅に減少する可能性があるため、働き方を計画的に考えることが大切です。
            </p>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="py-8">
          <h2 className="text-2xl font-bold text-gray-800 text-center mb-8">よくある質問 (FAQ)</h2>

          <div className="space-y-3">
            {/* Q1 */}
            <details className="group">
              <summary className="flex items-center justify-between w-full p-4 bg-white rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors list-none">
                <span className="font-medium text-gray-800 text-left pr-4">Q1: 交通費は103万円に含まれますか？</span>
                <svg className="w-5 h-5 text-gray-500 flex-shrink-0 transition-transform duration-200 group-open:rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <div className="mt-1 p-4 bg-gray-50 rounded-lg border border-gray-100">
                <div className="text-gray-600 leading-relaxed space-y-4">
                  <div>
                    <p className="font-semibold text-gray-700 mb-2">【税金（103万の壁）の場合】</p>
                    <p>原則として、交通費は「含まれません」。給与明細で交通費が区分されている場合、それは非課税扱いとなるため、純粋な「時給×時間」の合計だけで計算してください。</p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-700 mb-2">【社会保険（130万の壁）の場合】</p>
                    <p>こちら注意が必要です。社会保険の計算では、交通費も「収入」として「含まれます」。このシミュレーターの結果画面で「社会保険の壁」警告が出た場合は、交通費込みの金額で判断してください。</p>
                  </div>
                </div>
              </div>
            </details>

            {/* Q2 */}
            <details className="group">
              <summary className="flex items-center justify-between w-full p-4 bg-white rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors list-none">
                <span className="font-medium text-gray-800 text-left pr-4">Q2: 計算期間は「1月〜12月」ですか？「4月〜3月」ですか？</span>
                <svg className="w-5 h-5 text-gray-500 flex-shrink-0 transition-transform duration-200 group-open:rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <div className="mt-1 p-4 bg-gray-50 rounded-lg border border-gray-100">
                <div className="text-gray-600 leading-relaxed">
                  <p className="mb-3">税金の計算期間は<strong className="text-gray-800">「1月1日から12月31日」</strong>までです。</p>
                  <p>「年度（4月始まり）」ではないのでご注意ください。例えば、2026年の年収は、2026年1月1日〜12月31日までに受け取った給与の合計になります。</p>
                </div>
              </div>
            </details>

            {/* Q3 */}
            <details className="group">
              <summary className="flex items-center justify-between w-full p-4 bg-white rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors list-none">
                <span className="font-medium text-gray-800 text-left pr-4">Q3: バイトを掛け持ち（2つ以上）している場合は？</span>
                <svg className="w-5 h-5 text-gray-500 flex-shrink-0 transition-transform duration-200 group-open:rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <div className="mt-1 p-4 bg-gray-50 rounded-lg border border-gray-100">
                <div className="text-gray-600 leading-relaxed">
                  <p className="mb-3">すべてのアルバイト先の収入を<strong className="text-gray-800">「合計」</strong>する必要があります。</p>
                  <p>例えば、A社で60万、B社で50万稼ぐと、合計110万となり、103万円の壁を超えてしまいます。このシミュレーターを使う際は、すべてのバイトの時給と時間を合算して入力するか、合計の見込み年収を確認してください。</p>
                </div>
              </div>
            </details>

            {/* Q4 */}
            <details className="group">
              <summary className="flex items-center justify-between w-full p-4 bg-white rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors list-none">
                <span className="font-medium text-gray-800 text-left pr-4">Q4: 103万円を1円でも超えるとどうなりますか？</span>
                <svg className="w-5 h-5 text-gray-500 flex-shrink-0 transition-transform duration-200 group-open:rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <div className="mt-1 p-4 bg-gray-50 rounded-lg border border-gray-100">
                <div className="text-gray-600 leading-relaxed">
                  <p className="mb-3">あなた自身に<strong className="text-gray-800">所得税がかかります</strong>（超えた分に対して課税されます）。</p>
                  <p>さらに重要なのは、親や配偶者の税金です。あなたが扶養に入っている場合、親の税金控除（特定扶養控除など）がなくなり、親の税金が数万円〜十数万円増えてしまう可能性があります。「ギリギリ超えそう」な場合は、必ず親御さんに相談しましょう。</p>
                </div>
              </div>
            </details>
          </div>
        </div>

        {/* Footer */}
      </main>

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-200">
        <div className="max-w-2xl mx-auto px-4 py-8">
          <p className="text-sm text-gray-500 text-center mb-6">※ このシミュレーターは概算です。詳細は税理士にご相談ください。</p>
          <div className="flex flex-col items-center gap-4">
            <nav className="flex items-center gap-6">
              <Link to="/privacy" className="text-sm text-gray-500 hover:text-blue-600 transition-colors">プライバシーポリシー</Link>
              <Link to="/about" className="text-sm text-gray-500 hover:text-blue-600 transition-colors">運営者情報</Link>
              <Link to="/contact" className="text-sm text-gray-500 hover:text-blue-600 transition-colors">お問い合わせ</Link>
            </nav>
            <p className="text-sm text-gray-500">© 2026 Silent Code.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TaxSimulator;
